import {callProcedure} from '@/services/BigQueryService';
import type {GridData} from '@/components/DataTable/types';
import {getGenericGridData, type GridConfigOptions} from './gridBuilder';

function bqProcedureFetch(
  featureId: string,
  dataset: string,
  procedureName: string,
): Promise<Record<string, unknown>[]> {
  const env = process.env.VUE_APP_GCP_ENV === 'production' ? 'prod' : 'dev';
  return callProcedure(dataset, `${procedureName}_${env}`, [featureId], 'da-bmaps-layer-data');
}

const marketSubmarketSubtitle = (record: Record<string, unknown>) => {
  const market = record?.['Market'];
  const submarket = record?.['Submarket'];
  return ` - ${market} ${submarket ? `(${submarket})` : ''}`;
};

interface BqGridDefinition {
  dataset: string;
  procedureName: string;
  config: GridConfigOptions;
}

function defineBqGrid(definition: BqGridDefinition): (featureId: string) => GridData | null {
  return (featureId: string) => {
    if (!featureId) {
      console.error(`No feature ID provided for ${definition.config.title}`);
      return null;
    }
    return getGenericGridData(
      bqProcedureFetch(featureId, definition.dataset, definition.procedureName),
      definition.config,
    );
  };
}

export const getMultifamilyMarketSubmarketGrid = defineBqGrid({
  dataset: 'da_multifamily_data',
  procedureName: 'da_get_cluster_data',
  config: {
    title: 'Market/Submarket Data',
    mappingKey: 'MF Market and Submarket',
    hiddenColumns: ['bmaps_poly', 'Market', 'Submarket'],
    hiddenFields: ['Period', 'bmaps_poly', 'Market', 'Submarket', 'Ranking Description'],
    subtitle: marketSubmarketSubtitle,
  },
});

export const getMarketSubmarketGrid = defineBqGrid({
  dataset: 'da_cluster_data',
  procedureName: 'da_get_cluster_data',
  config: {
    title: 'Market/Submarket Data',
    mappingKey: 'Industrial Markets and Submarkets',
    hiddenColumns: ['bmaps_poly_id', 'Market', 'Submarket'],
    hiddenFields: ['Period', 'bmaps_poly_id', 'Market', 'Submarket', 'Ranking Description'],
    subtitle: marketSubmarketSubtitle,
  },
});

export const getExitCapRateGrid = defineBqGrid({
  dataset: 'da_cap_rates',
  procedureName: 'da_get_exit_cap_rate_data',
  config: {
    title: 'Exit Cap Rate Data',
    mappingKey: 'Exit Cap Rate',
    hiddenColumns: ['bmaps_poly', 'Market', 'Segment'],
    hiddenFields: ['bmaps_poly'],
    subtitle: (record: Record<string, unknown>) => {
      const market = record?.['Market'];
      return market ? ` - ${market}` : '';
    },
  },
});
